import React, { useCallback, useEffect, useState } from "react";
import { X, Save, ShieldOff, ShieldCheck, Calendar, Check } from "lucide-react";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

/**
 * Admin dialog for promoting an athlete to the `volunteer` role and picking
 * which events they can score.
 *
 *   mode="assign"  → POST /admin/users/{id}/volunteer  {event_ids}
 *   mode="revoke"  → DELETE /admin/users/{id}/volunteer
 *
 * Non-admins never see this — the button is gated in Admin.jsx.
 */
export default function VolunteerRoleDialog({ target, onClose, onSaved }) {
  const isVolunteer = !!target?.is_volunteer;
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(target?.assigned_event_ids || []),
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Admin can hit /admin/events (paginated); pull a healthy page so the
      // volunteer can be assigned to any past / live / upcoming event.
      const { data } = await api.get("/events?page=1&page_size=100");
      setEvents(data.events || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.id}/volunteer`, {
        event_ids: Array.from(selectedIds),
      });
      toast.success(`${target.name} · volunteer flag active (${selectedIds.size} event${selectedIds.size === 1 ? "" : "s"})`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm(`Revoke the volunteer flag from ${target.name}? They remain an athlete but will lose Scoring Deck access.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${target.id}/volunteer`);
      toast.success(`${target.name} · volunteer flag revoked`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] grid place-items-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="volunteer-role-dialog"
    >
      <GlassPanel
        className="p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto relative"
        scanline
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-mute hover:text-white"
          aria-label="Close"
          data-testid="volunteer-role-dialog-close"
        >
          <X size={18} />
        </button>

        <div className="mb-5">
          <div className="gps-label inline-flex items-center gap-2">
            <ShieldCheck size={12} /> {isVolunteer ? "MANAGE VOLUNTEER" : "MAKE VOLUNTEER"}
          </div>
          <h3 className="gps-heading text-xl text-white mt-1">{target.name}</h3>
          <div className="text-mute gps-mono text-[10px] tracking-[0.2em] mt-1">
            {target.public_id || "—"} · {target.email} · {(target.role || "athlete").toUpperCase()}
            {isVolunteer && <span className="text-cyan-400"> · VOLUNTEER FLAG ACTIVE</span>}
          </div>
        </div>

        <div className="mb-3 gps-mono text-[11px] tracking-[0.2em] text-mute">
          ASSIGN EVENTS · A FLAGGED VOLUNTEER CAN ONLY SCORE THE EVENTS TICKED BELOW · THEY STAY AN ATHLETE THROUGHOUT
        </div>

        {loading ? (
          <div className="p-8 text-center gps-mono text-mute text-xs tracking-[0.2em]">LOADING EVENTS…</div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center gps-mono text-mute text-xs tracking-[0.2em]">NO EVENTS YET · CREATE ONE FIRST</div>
        ) : (
          <div
            className="border border-white/10 rounded-sm max-h-[50vh] overflow-y-auto"
            data-testid="volunteer-role-events"
          >
            {events.map((ev) => {
              const checked = selectedIds.has(ev.id);
              const dateStr = ev.date ? new Date(ev.date).toLocaleDateString(undefined, {
                year: "numeric", month: "short", day: "2-digit",
              }) : "TBD";
              return (
                <label
                  key={ev.id}
                  className={`flex items-start gap-3 p-3 border-b border-white/5 cursor-pointer transition-colors ${
                    checked ? "bg-cyan-400/[0.10]" : "hover:bg-white/[0.04]"
                  }`}
                  data-testid={`volunteer-role-event-${ev.id}`}
                >
                  <input
                    type="checkbox"
                    className="accent-cyan-400 mt-1 cursor-pointer"
                    checked={checked}
                    onChange={() => toggle(ev.id)}
                    data-testid={`volunteer-role-event-toggle-${ev.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm">{ev.name}</div>
                    <div className="gps-mono text-[10px] tracking-[0.15em] mt-1 flex flex-wrap gap-3 text-mute">
                      <span className="inline-flex items-center gap-1"><Calendar size={10} /> {dateStr}</span>
                      <span className={
                        ev.status === "live" ? "text-emerald-400"
                        : ev.status === "upcoming" ? "text-cyan-400"
                        : "text-mute"
                      }>{(ev.status || "").toUpperCase()}</span>
                      {ev.location && <span className="truncate">{ev.location}</span>}
                    </div>
                  </div>
                  {checked && <Check size={14} className="text-cyan-400 mt-1" />}
                </label>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex gap-2 flex-wrap justify-between">
          <div>
            {isVolunteer && (
              <button
                onClick={revoke}
                className="gps-btn ghost"
                disabled={busy}
                data-testid="volunteer-role-revoke"
              >
                <ShieldOff size={14} /> REVOKE FLAG
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="gps-btn ghost"
              disabled={busy}
              data-testid="volunteer-role-cancel"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              className="gps-btn"
              disabled={busy || loading}
              data-testid="volunteer-role-save"
            >
              <Save size={14} /> {busy ? "SAVING…" : (isVolunteer ? "UPDATE ASSIGNMENTS" : "MAKE VOLUNTEER")}
            </button>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}
